import queryString from 'query-string';
import type { match as ReactRouterMatch } from 'react-router-dom';
import { all, call, take, takeLatest, put, select } from 'redux-saga/effects';

import type { QuoteQpvData } from '@frontend/api';
import { wait } from '@frontend/utils';

import { VIEWER_MODES } from 'constants/all';

import {
  types as cadViewerTypes,
  resetView as resetCADViewerView,
} from 'modules/common/viewer/cadViewer/data/actions';
import { getViewerPendingPartFileUuids } from 'modules/common/viewer/cadViewer/data/reducer';
import { getActiveFile } from 'modules/common/viewer/data/reducer';
import { fetchProcesses } from 'modules/processes/data/sagas';
import * as collaboratorsSagas from 'modules/quotes/collaborators/data/sagas';
import { getBestSetupPartFileUuid } from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/PartSetupStepper';
import { partLibraryViewerPage } from 'modules/viewerPage/data/actions';
import { getPartFileByUUID } from 'modules/viewerPage/data/reducer';
import {
  getMultiLevelBom,
  getPartInformation,
} from 'modules/viewerPage/data/reducer';
import { initPartLibraryViewer } from 'modules/viewerPage/data/sagas';

import quoteService from 'services/quoteService';

import { initMaterialStore } from 'store/materials/sagas';
import { getGroupId } from 'store/session/selectors';

import history from 'utils/browserHistory';

import { quotePartViewer } from './actions';
import { types } from './actions';
import type { QuoteItems, QuoteItem } from './reducer';
import {
  getExportControlled,
  getQuoteId,
  getQuoteItem,
  getQuoteItems,
  getQuoteNum,
  getQuoteRevision,
  getQuoteStatus,
} from './reducer';

export type QuoteViewerRouteParams = {
  quoteNum: string; //quote number and revision (separated by `-`)
};

export type QuoteViewerSearchTail = {
  //The following are for the regular part viewer (partLibraryViewer)
  annotation_id?: string;
  channel_id?: string;
  active_part?: string;
  material_selected?: string;
  search?: string;
  selected_file?: string;
};
export type QuoteViewerSearch = {
  quote_item_id?: string;
} & QuoteViewerSearchTail;

export default function* root() {
  yield takeLatest(types.RECEIVE_QUOTE_ITEM_ID, watchQuoteItemId);
  yield takeLatest(types.TAKE_OVER_QUOTE_ITEM, watchTakeOverQuoteItem);
}

const quoteNumAndRevisionRegex = /(\d+)(-(\d+))?/;
const parseQuoteNumAndRevision = (
  quoteNumAndRevision: string,
): { quoteNum?: number; quoteRevision?: number } => {
  quoteNumAndRevisionRegex.lastIndex = 0; //reset;
  const matches = quoteNumAndRevision.match(quoteNumAndRevisionRegex);
  if (matches) {
    const quoteNum = parseInt(matches[1], 10);
    const quoteRevision = parseInt(matches[3], 10) || undefined; //can be NaN, so `||` instead of `??`
    return { quoteNum, ...(quoteRevision && { quoteRevision }) };
  }
  return {}; //unexpected
};

function* updateQuoteItemsStatusAndExportControlled(
  quoteNum: number,
  quoteRevision?: number,
) {
  let currentQuoteItems: QuoteItems = yield select(getQuoteItems);
  const groupId: string = yield select(getGroupId);
  const quoteData: QuoteQpvData = yield call(
    quoteService.quoteViewer.get,
    quoteNum,
    { group: groupId, ...(quoteRevision && { revisionNumber: quoteRevision }) },
  );
  if (
    quoteData?.quoteItems.length !== currentQuoteItems?.size ||
    new Set<number>([
      ...quoteData.quoteItems.map((i) => i.id),
      ...currentQuoteItems.keys(),
    ]).size !== currentQuoteItems?.size
  ) {
    //Then there is a change in the quoteItems, so update:
    //* Create quoteItems Map. QuoteItem are index by their `id`. And insertion order is by their `position`
    const quoteItemsJson = quoteData.quoteItems.sort(
      (a, b) => a.position - b.position,
    );
    const quoteItems: QuoteItems = new Map();
    let item: QuoteItem | undefined;
    for (const itemJson of quoteItemsJson) {
      const thisItem: QuoteItem = {
        ...itemJson,
        ...(item && { previous: item }),
      };
      if (item) {
        //Set next on the previous item before updating `item`
        item.next = thisItem;
      }
      item = thisItem;
      quoteItems.set(item.id, item);
    }
    yield put(quotePartViewer.receiveQuoteItems({ quoteItems }));

    currentQuoteItems = quoteItems;
  }

  const currentQuoteStatus: string | undefined = yield select(getQuoteStatus);
  if (currentQuoteStatus !== quoteData.status) {
    yield put(
      quotePartViewer.receiveQuoteStatus({ quoteStatus: quoteData.status }),
    );
  }

  const currentQuoteId: number | undefined = yield select(getQuoteId);
  if (currentQuoteId !== quoteData.id) {
    yield put(quotePartViewer.receiveQuoteId({ id: quoteData.id }));
  }

  const currentExportControlled: string | undefined = yield select(
    getExportControlled,
  );
  if (!!currentExportControlled !== !!quoteData.exportControlled) {
    yield put(
      quotePartViewer.receiveExportControlled({
        exportControlled: !!quoteData.exportControlled,
      }),
    );
  }

  return currentQuoteItems;
}

export function* initQuotePartViewer({
  location,
  match,
}: {
  location: Location;
  match: ReactRouterMatch<QuoteViewerRouteParams>;
}) {
  const {
    params: { quoteNum: quoteNumAndRevision },
  } = match;
  const { quoteNum, quoteRevision } =
    parseQuoteNumAndRevision(quoteNumAndRevision);
  if (!quoteNum) {
    return;
  }

  yield all([call(initMaterialStore), call(fetchProcesses)]);

  const { quote_item_id: quoteItemIdString, ...searchTail } = queryString.parse(
    location.search,
  ) as QuoteViewerSearch;

  const currentQuoteItems: QuoteItems = yield call(
    updateQuoteItemsStatusAndExportControlled,
    quoteNum,
    quoteRevision,
  );

  const quoteItemId = parseInt(quoteItemIdString ?? '', 10);
  let quoteItem = currentQuoteItems.get(quoteItemId);
  if (!quoteItem) {
    //Otherwise default quoteItemId is the first quote item's id
    const [firstQuoteItem] = currentQuoteItems.values(); //recall the insertion order is by position
    if (!firstQuoteItem || !firstQuoteItem.id) {
      return;
    }
    quoteItem = firstQuoteItem;
    //Replace URL with the default quoteItemId
    history.replace(
      `${location.pathname}?${queryString.stringify({
        quote_item_id: quoteItem.id,
      })}`,
    );
    return;
  }

  const { id, partMasterUuid, partMasterVersion } = quoteItem;

  yield all([
    //quoteItemId is sent with init() rather than receiveQuoteItemId because we don't want to trigger `watchQuoteItemId`
    //and we want to initPartLibraryViewer() as apart of `initQuotePartViewer` for the quote part viewer route
    put(
      quotePartViewer.init({
        quoteNum,
        quoteRevision,
        quoteItemId: quoteItem.id,
      }),
    ),
    call(initPartLibraryViewer, {
      location: {
        ...location,
        search: queryString.stringify({
          ...searchTail,
          version: partMasterVersion,
          quote_item_id: id,
        }),
      },
      match: {
        params: {
          mode: VIEWER_MODES.OWNER,
          partMasterUUID: partMasterUuid,
        },
      },
    }),
  ]);
}

function* watchQuoteItemId(action: {
  payload: {
    quoteItemId: number;
    partSetupState?: number; //bitmask number. See `partSetupState.ts`
    gotoLast?: boolean;
  };
}) {
  const { partSetupState, gotoLast } = action.payload;
  //use change in quoteItemId as opportunity to update the quoteItems, quote status and exportControlled state
  const quoteNum: number = yield select(getQuoteNum);
  const quoteRevision: number | undefined = yield select(getQuoteRevision);
  yield call(
    updateQuoteItemsStatusAndExportControlled,
    quoteNum,
    quoteRevision,
  );
  //quoteItemId should already be set by reducer for `types.RECEIVE_QUOTE_ITEM_ID`, so getQuoteItem()
  //should return result with `quoteItemId === quoteItem.id`
  const quoteItem: QuoteItem = yield select(getQuoteItem);
  if (!quoteItem) {
    return;
  }

  const { id, partMasterUuid, partMasterVersion } = quoteItem;

  //Toggle viewer disabled and enabled to unmount it (so dispose methods are called) and reload it
  yield put(quotePartViewer.disableViewer());
  yield put(quotePartViewer.enableViewer());
  yield call(initPartLibraryViewer, {
    location: {
      ...location,
      search: queryString.stringify({
        version: partMasterVersion,
        quote_item_id: id,
        part_setup: partSetupState,
        //No `...searchTail` because it no longer applies for new quoteItemId
      }),
    },
    match: {
      params: {
        mode: VIEWER_MODES.OWNER,
        partMasterUUID: partMasterUuid,
      },
    },
  });

  const rootPartFileUuids: string[] = [];
  if (gotoLast) {
    //Find the last part file uuid to set as active
    //* First, create FlatBOM from multilevel bom
    //* Get the lastPart from the FlatBOM
    //* Get the best partFileUuid for the last Part (there can be more than one file that describes the part)
    //* Finally set the active Uuid as the best partFileUuid for the last part from the FlatBOM
    const multiLevelBom = yield select(getMultiLevelBom);
    const flattenedBomPartIds: Set<string> = new Set();
    const bomStack = [multiLevelBom];

    while (bomStack.length) {
      const bomNode = bomStack.shift();
      if (!bomNode?.partUuid) {
        break;
      }
      // Only add children if node is not already in Set -- prevents adding duplicate children to stack
      if (
        bomNode.children?.length &&
        !flattenedBomPartIds.has(bomNode.partUuid)
      ) {
        bomStack.push(...bomNode.children);
      }
      flattenedBomPartIds.add(bomNode.partUuid);
    }

    const flatBomIds = Array.from(flattenedBomPartIds);
    const lastPartUuid = flatBomIds[flatBomIds.length - 1];
    const partInformation = (yield select(getPartInformation)) || [];
    const lastPart = partInformation.find(({ uuid }) => uuid === lastPartUuid);
    const bestFileUuidForLastPart = getBestSetupPartFileUuid(lastPart);
    const bestPartFile = yield select(
      getPartFileByUUID,
      bestFileUuidForLastPart,
    );

    //Now find the rootPartFileUuids for the bestPartFile (there can be > 1 if the root part has supporting files!)
    const partFileStack = [bestPartFile];
    let currentPartFile;
    while ((currentPartFile = partFileStack.pop())) {
      if (currentPartFile.isRootPart) {
        rootPartFileUuids.push(currentPartFile.uuid);
      } else if (currentPartFile.assemblyParentFileUuid) {
        const parentPartFile = partInformation.find(({ partFiles }) =>
          partFiles.some(
            ({ uuid }) => uuid === currentPartFile.assemblyParentFileUuid,
          ),
        );
        if (parentPartFile) {
          partFileStack.push(...parentPartFile.partFiles);
        }
      }
    }

    yield put(
      partLibraryViewerPage.setActiveUUID({
        forceFocus: true,
        partFileUuid: bestFileUuidForLastPart,
      }),
    );
    //setActiveUUID triggers watchSetActiveUUID() and it takes time for that saga to finish
    //Need to wait for setActiveFile() to finish!
    while (true) {
      const currentActiveFile = yield select(getActiveFile);
      if (currentActiveFile?.uuid === bestFileUuidForLastPart) {
        break;
      }
      yield wait(0); //defer and try again. It will be set soon because of previous setActiveUuid
    }
  }

  const activeFile: undefined | { hasGeometry: boolean; uuid: string } =
    yield select(getActiveFile);

  if (activeFile && activeFile.hasGeometry) {
    //Wait for the activeFile to be loaded
    //* cadViewerTypes.VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS means `onLoadPartFile()` was called by part viewer's renderer.loadPartData()
    //  Note: sometimes there can be greater than one call to `onLoadPartFile()`. We care about it being called for the activeFile
    //* This means the parts, assemblies, bodies & assemblyInstances for the `partMasterUuid` are loaded.
    //* It does not mean the part's geometry or body's geometry is loaded... It just means the structure is loaded and it is ready
    let receivedActiveFile = false;
    while (true) {
      const {
        payload: { partFileUuid },
      } = yield take(cadViewerTypes.VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS);

      if (
        partFileUuid === activeFile.uuid ||
        rootPartFileUuids.some((uuid) => uuid === partFileUuid)
      ) {
        receivedActiveFile = true;
      }

      const pendingUuids: string[] = yield select(
        getViewerPendingPartFileUuids,
      );
      if (pendingUuids.length === 0 && receivedActiveFile) {
        break;
      }
    }
    yield put(resetCADViewerView());
  }
}

function* watchTakeOverQuoteItem(action) {
  const { quoteItemId } = action.payload;
  const { isCancelled } = yield call(
    collaboratorsSagas.acquireLock,
    quoteItemId,
  );

  if (isCancelled) return;
}
